import { useEffect, useRef, useState, type FormEvent } from 'react';
import { deleteRow, listCollection, saveRow, type Row } from '../../lib/db';
import { uploadToStorage, storageUrlFromPath } from '../../lib/upload';
import { useToast } from '../../components/Toast';

export function LogoList() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { notify } = useToast();

  const load = () =>
    listCollection('logos', 'createdAt').then((r) => {
      setRows(r);
      setLoading(false);
    });
  useEffect(() => {
    void load();
  }, []);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || !name.trim()) return;
    setUploading(true);
    try {
      const id = await saveRow('logos', undefined, { name: name.trim(), path: '', version: null });
      const { path } = await uploadToStorage('logos', id, file);
      await saveRow('logos', id, { path, version: String(Date.now()) });
      setName('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      notify('success', 'Logo uploaded.');
      void load();
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this logo?')) return;
    await deleteRow('logos', id);
    notify('success', 'Logo deleted.');
    void load();
  };

  return (
    <>
      <h3 className="fw-bold mb-3">Partner logos</h3>
      <form onSubmit={add} className="d-flex gap-2 align-items-end mb-4" style={{ maxWidth: 640 }}>
        <div className="flex-grow-1">
          <label className="form-label">Name</label>
          <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="flex-grow-1">
          <label className="form-label">File</label>
          <input
            ref={fileRef}
            type="file"
            className="form-control"
            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            required
          />
        </div>
        <button className="btn btn-success" disabled={uploading} type="submit">
          {uploading ? 'Uploading…' : 'Add'}
        </button>
      </form>

      {loading ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <div className="d-flex flex-wrap gap-4">
          {rows.map((r) => {
            const url = storageUrlFromPath(String(r.path ?? ''));
            return (
              <div key={r.id} className="text-center">
                {url && (
                  <img src={url} alt={String(r.name)} style={{ height: 48, objectFit: 'contain' }} />
                )}
                <div className="small">{String(r.name)}</div>
                <button className="btn btn-sm btn-outline-danger mt-1" onClick={() => remove(r.id)}>
                  Delete
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
